// packages/api/src/hooks/utils/socket-shared.ts
//
// Phase 12 analista-queue-kpis-notif Plan 11 — Socket.IO shared instance.
//
// useSocketRoom + useSocketEvent compartilham um único client Socket.IO por aba
// (lazy-init via access token). Pattern espelha `useAdminRegistrationsLive`
// (Phase 01 W3.10) mas centralizado para múltiplos consumers (war-room +
// proposal detail + qualquer feature realtime futura).
//
// **Design rationale (vs context provider):**
// - Apps maiores teriam SocketProvider em App.tsx; Alamo MVP não tem ainda.
// - Cria socket lazy quando primeiro hook é montado; reusa a mesma instância.
// - Disconnect quando todos os consumers unmount (refcount).
// - Token rotation: backend (Phase 09) faz JWT handshake — auth no connect.

import { io, type Socket } from 'socket.io-client';

import { getAccessToken } from '../../client';

/**
 * Resolve a URL do Socket.IO. Precedência:
 *   1. `VITE_SOCKET_URL` (override explícito).
 *   2. `VITE_API_URL` (back-compat — base da API com sufixo `/api`).
 *   3. `VITE_API_BASE_URL` SE for absoluta (http/https) → usa só a ORIGIN
 *      (strip do path, ex. `/api/v1`). É o caso do build de homolog/prod, onde
 *      a distribuição de API serve `/socket.io/*` na mesma origin.
 *   4. Default `http://localhost:3006` (dev local).
 *
 * O guard de "absoluta" é crítico: em dev `VITE_API_BASE_URL` é relativa
 * (`/api/v1`, servida pelo proxy do Vite); derivar origin daí daria a origin do
 * web (`:5176`), onde NÃO há gateway de socket. Nesse caso caímos no default
 * `localhost:3006`, onde a API roda.
 */
export function resolveSocketUrl(): string {
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};
  const explicit = env.VITE_SOCKET_URL;
  if (explicit) return explicit;
  const apiUrl = env.VITE_API_URL;
  if (apiUrl) return apiUrl.replace(/\/api\/?$/, '');
  const apiBaseUrl = env.VITE_API_BASE_URL;
  if (apiBaseUrl && /^https?:\/\//i.test(apiBaseUrl)) {
    try {
      return new URL(apiBaseUrl).origin;
    } catch {
      // URL malformada — cai no fallback abaixo.
    }
  }
  // Dev: conecta same-origin para o WS passar pelo proxy do Vite (/socket.io),
  // alcançável de qualquer IP da rede. Build (DEV=false) mantém localhost.
  if (env.DEV && typeof window !== 'undefined') return window.location.origin;
  return 'http://localhost:3006';
}

let sharedSocket: Socket | null = null;
let refCount = 0;

/**
 * Obtém (lazy-init) o socket.io-client compartilhado. Incrementa refcount —
 * caller deve invocar `releaseSharedSocket()` em cleanup.
 *
 * Retorna `null` quando não autenticado (sem access token) — hooks viram no-op.
 */
export function acquireSharedSocket(): Socket | null {
  const token = getAccessToken();
  if (!token) return null;

  if (!sharedSocket) {
    try {
      const socket = io(resolveSocketUrl(), {
        transports: ['websocket'],
        auth: { token },
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1500,
      });
      // socket.auth é lido no momento do handshake. O access token expira em
      // 15min, então numa reconexão o token capturado no io() estaria velho e o
      // backend rejeitaria com AUTH_TOKEN_EXPIRED. Atualizar auth antes de cada
      // tentativa (e ao receber connect_error) injeta o token fresco que o
      // interceptor de auth já rotacionou.
      socket.io.on('reconnect_attempt', () => {
        const fresh = getAccessToken();
        if (fresh) socket.auth = { token: fresh };
      });
      socket.on('connect_error', () => {
        const fresh = getAccessToken();
        if (fresh) socket.auth = { token: fresh };
      });
      sharedSocket = socket;
    } catch {
      // Defensive — io() is permissive but throwing would crash the page.
      sharedSocket = null;
      return null;
    }
  }
  refCount += 1;
  return sharedSocket;
}

/**
 * Decrement refcount. Disconnect quando o último consumer unmount.
 */
export function releaseSharedSocket(): void {
  if (!sharedSocket) return;
  refCount = Math.max(0, refCount - 1);
  if (refCount === 0) {
    try {
      sharedSocket.disconnect();
    } catch {
      // ignore
    }
    sharedSocket = null;
  }
}

/**
 * Reset interno para testes — limpa estado entre `beforeEach` runs.
 *
 * **Não usar em código de produção.**
 */
export function __resetSharedSocketForTests(): void {
  sharedSocket = null;
  refCount = 0;
}
